import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { useAuth } from './auth/AuthContext';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AuditPage } from './pages/AuditPage';
import { DataExplorerPage } from './pages/DataExplorerPage';
import { BillingPage } from './pages/BillingPage';
import { LoginPage } from './pages/LoginPage';
import { OverviewPage } from './pages/OverviewPage';
import { SchoolDetailPage } from './pages/SchoolDetailPage';
import { SchoolsPage } from './pages/SchoolsPage';
import { SystemHealthPage } from './pages/SystemHealthPage';
import { TicketsPage } from './pages/TicketsPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)' }}>
        <div className="caption">Restoring your session…</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  // The overview is school data. An admin without that permission gets sent to
  // the first page they can actually use, rather than a 403 on their landing page.
  if (location.pathname === '/' && !user.permissions.schoolData) {
    return <Navigate to={user.permissions.tickets ? '/tickets' : '/system-health'} replace />;
  }

  return <AppShell>{children}</AppShell>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <OverviewPage />
          </RequireAuth>
        }
      />
      <Route
        path="/schools"
        element={
          <RequireAuth>
            <SchoolsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/schools/:id"
        element={
          <RequireAuth>
            <SchoolDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireAuth>
            <BillingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/analytics"
        element={
          <RequireAuth>
            <AnalyticsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/data-explorer"
        element={
          <RequireAuth>
            <DataExplorerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/system-health"
        element={
          <RequireAuth>
            <SystemHealthPage />
          </RequireAuth>
        }
      />
      <Route
        path="/tickets"
        element={
          <RequireAuth>
            <TicketsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/audit"
        element={
          <RequireAuth>
            <AuditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin-users"
        element={
          <RequireAuth>
            <AdminUsersPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
